import { PORTRAIT_BASE_PROMPT } from '../constants/portrait';

export const INHERITED_STYLE_STORAGE_KEY = 'profileStudio_inheritedStyle';

export type InheritedStyleSettings = {
  mode?: 'musician' | 'standard' | 'portrait';
  // 一般版
  backgroundOption?: string;
  selectedOutfitId?: string;
  selectedPoseId?: string;
  customBackground?: string;
  customOutfitText?: string;
  // 音楽家版
  backgroundId?: string;
  dressId?: string;
  sleeveId?: string;
  itemId?: string;
  poseId?: string;
  customBackgroundText?: string;
  customDressText?: string;
  customSleeveText?: string;
  customItemText?: string;
  // ポートレート版
  userPrompt?: string;
  // 旧フォーマット（GalleryModal 経由）
  background?: string;
  outfit?: string;
  pose?: string;
};

export type InheritedStylePayload = {
  showcaseId?: string;
  prompt?: string;
  imageUrl?: string;
  settings?: InheritedStyleSettings | null;
  savedAt?: string;
};

/** 保存済みの全文プロンプトから、ユーザー入力部分だけ取り出す */
export function extractPortraitUserPrompt(fullPrompt?: string | null): string {
  if (!fullPrompt || typeof fullPrompt !== 'string') return '';
  const trimmed = fullPrompt.trim();
  if (!trimmed) return '';

  const base = PORTRAIT_BASE_PROMPT.trim();
  if (trimmed === base) return '';

  if (trimmed.startsWith(base)) {
    return trimmed.slice(base.length).replace(/^\s+/, '');
  }

  // ベースが途中で切れている古いキャッシュ向け: 二重改行以降をユーザー部とする
  const split = trimmed.split(/\n\n+/);
  if (split.length >= 2 && /identity reference/i.test(split[0])) {
    return split.slice(1).join('\n\n').trim();
  }

  return trimmed;
}

/** 新旧 settings を正規化してアプリが使える形にする */
export function normalizeInheritedSettings(
  settings?: InheritedStyleSettings | null,
  fallbackPrompt?: string | null,
): InheritedStyleSettings {
  const s = settings || {};
  const mode =
    s.mode === 'musician' ? 'musician' : s.mode === 'portrait' ? 'portrait' : 'standard';

  if (mode === 'musician') {
    return {
      mode: 'musician',
      backgroundId: s.backgroundId || s.background || 'studio_white',
      dressId: s.dressId || s.outfit || 'dress_black',
      sleeveId: s.sleeveId || 'any',
      itemId: s.itemId || 'none',
      poseId: s.poseId || s.pose || 'front',
      customBackgroundText: s.customBackgroundText || s.customBackground || '',
      customDressText: s.customDressText || s.customOutfitText || '',
      customSleeveText: s.customSleeveText || '',
      customItemText: s.customItemText || '',
    };
  }

  if (mode === 'portrait') {
    return {
      mode: 'portrait',
      userPrompt: (s.userPrompt || '').trim() || extractPortraitUserPrompt(fallbackPrompt),
    };
  }

  return {
    mode: 'standard',
    backgroundOption: s.backgroundOption || s.background || 'default',
    selectedOutfitId: s.selectedOutfitId || s.outfit || 'suit_blue',
    selectedPoseId: s.selectedPoseId || s.pose || 'front',
    customBackground: s.customBackground || '',
    customOutfitText: s.customOutfitText || '',
  };
}

export function saveInheritedStyle(payload: InheritedStylePayload): void {
  try {
    const data: InheritedStylePayload = {
      ...payload,
      settings: normalizeInheritedSettings(payload.settings, payload.prompt),
      savedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(INHERITED_STYLE_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('継承スタイルの保存に失敗:', error);
  }
}

export function loadInheritedStyle(): InheritedStylePayload | null {
  try {
    const raw = sessionStorage.getItem(INHERITED_STYLE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as InheritedStylePayload;
    return {
      ...parsed,
      settings: normalizeInheritedSettings(parsed.settings, parsed.prompt),
    };
  } catch (error) {
    console.warn('継承スタイルの読み込みに失敗:', error);
    return null;
  }
}

export function clearInheritedStyle(): void {
  try {
    sessionStorage.removeItem(INHERITED_STYLE_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getInheritedStyleTargetPath(settings?: InheritedStyleSettings | null): string {
  const normalized = normalizeInheritedSettings(settings);
  if (normalized.mode === 'musician') {
    return '/musician?inheritStyle=1';
  }
  if (normalized.mode === 'portrait') {
    return '/portrait?inheritStyle=1';
  }
  return '/?mode=app&inheritStyle=1';
}

export function inheritStyleAndNavigate(payload: InheritedStylePayload): void {
  saveInheritedStyle(payload);
  window.location.href = getInheritedStyleTargetPath(payload.settings);
}
